import { computed, defineComponent, inject, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import html_iway from "./template.iway.html?raw";
import { currentOrderProviderKey } from "../../../infrastructure/current_order_provider";
import { orderServiceKey } from "../../../infrastructure/gfo_api/order_service";
import type { AccessDslConfigPatchRequest } from "../../../infrastructure/gfo_api/order_api";
import { ApiValidationError } from "../../../infrastructure/gfo_api/api_validation_error";
import ErrorMessage from "../../Shared/ErrorMessage";

const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("iway" == app_theme) {
  html = html_iway;
} else if ("lic" == app_theme) {
  html = html_iway;
} else if ("sak" == app_theme) {
  html = html_iway;
}
export default defineComponent({
  template: html,
  components: { ErrorMessage },
  emits: {
    next: () => true
  },
  setup() {
    // Injections
    const { t } = useI18n();
    const orderService = inject(orderServiceKey);
    const currentOrderProvider = inject(currentOrderProviderKey);

    // vars und computed
    const config = computed(() => {
      return currentOrderProvider?.currentOrder?.value?.configs.find((c) => c.type == 'AccessDslConfig' || c.type == 'AccessFiberConfig') ?? null
    });
    const generalError = ref("");
    const dslOrderType = ref("");
    const dslOrderTypeError = ref("");
    const dslNumber = ref<string | null>(null);
    const dslNumberError = ref("");
    const dslTenant = ref<string | null>(null);
    const dslTenantError = ref("");
    const dslOrderTypeChoices = computed(() => {
      let choices = [];
      choices.push({
        key: "new",
        text: t("ConfigsAccess_PreviousTenant_DslNew"),
      });
      choices.push({
        key: "provider_change",
        text: t("ConfigsAccess_PreviousTenant_DslProviderChange"),
      });
      return choices;
    });

    // methods

    // laufender Autosave-Request; validate() muss darauf warten, bevor es den
    // (sonst optimistisch geleerten) Fehlerstatus auswertet
    let pendingSave: Promise<void> | null = null;

    const save = async () => {
      const run = async () => {
        dslOrderTypeError.value = "";
        dslNumberError.value = "";
        dslTenantError.value = "";
        generalError.value = "";
        const conf = config.value as any;
        if (conf == null || orderService == null) {
          return;
        }
        if (conf.type == "AccessDslConfig"){
          const request: AccessDslConfigPatchRequest = {
            dsl_number:dslNumber.value,
            dsl_tenant:dslTenant.value,
            dsl_order_type:dslOrderType.value,
          };
          try {
            await orderService.patchConfig(conf.uuid, conf.type, request);
          } catch(err){
            if (err instanceof ApiValidationError) {
              dslOrderTypeError.value = err.errors["dsl_order_type"]?.[0] ?? "";
              dslNumberError.value = err.errors["dsl_number"]?.[0] ?? "";
              dslTenantError.value = err.errors["dsl_tenant"]?.[0] ?? "";
            } else {
              generalError.value = `${err}`;
            }
          }
        }
      };
      pendingSave = run();
      await pendingSave;
    }

    const onDslOrderTypeChange = async (_evt: Event) => {
      if (dslOrderType.value == "new"){
        dslNumber.value = null;
        await save();
      } else {
        // nicht speichern, erst wenn nummer gesetzt wird
      }
       
    }

    const onDslNumberChange = async (_evt: Event) => {
      await save();
    }

    const isValid = computed(() => {
      if (!dslOrderType.value) {
        return false;
      }
      if (dslOrderType.value === "provider_change" && !dslNumber.value) {
        return false;
      }
      return !dslOrderTypeError.value && !dslNumberError.value;
    });

    const validate = async () => {
      if (pendingSave) {
        await pendingSave;
      }
      if (!dslOrderType.value) {
        dslOrderTypeError.value = t("Validation_Field_Required");
      }
      if (dslOrderType.value === "provider_change" && !dslNumber.value) {
        dslNumberError.value = t("Validation_Field_Required");
      }
      return isValid.value;
    };

    onMounted(function(){
      const conf = config.value as any;
      if (conf == null){
        return;
      }
      dslOrderType.value = conf.data?.dsl_order_type ?? "";
      dslNumber.value = conf.data?.dsl_number ?? null;
      dslTenant.value = conf.data?.dsl_tenant ?? null;
    })

    return {
      t,
      generalError,
      dslOrderType,
      dslOrderTypeError,
      dslOrderTypeChoices,
      dslNumber,
      dslNumberError,
      dslTenant,
      dslTenantError,
      onDslOrderTypeChange,
      onDslNumberChange,
      save,
      isValid,
      validate,
    };
  },
} as any);
